#version 300 es

precision highp float;

// MIT License - Copyright (c) 2025 Jakob Thomsen, 2025 ClassDojo
// Based on https://www.shadertoy.com/view/wcKSRh

in vec2 vUv;
out vec4 fragColor;

uniform int uSubdiv;
uniform vec2 uResolution;
uniform sampler2D uColorTexture;
uniform sampler2D uNoiseTexture;

#define pi 3.1415926
#define tau (pi+pi)
#define DIST_MAX 1000.0

mat3 yaw_pitch_roll(float yaw, float pitch, float roll) {
    mat3 R = mat3(vec3(cos(yaw), sin(yaw), 0.0), vec3(-sin(yaw), cos(yaw), 0.0), vec3(0.0, 0.0, 1.0));
    mat3 S = mat3(vec3(1.0, 0.0, 0.0), vec3(0.0, cos(pitch), sin(pitch)), vec3(0.0, -sin(pitch), cos(pitch)));
    mat3 T = mat3(vec3(cos(roll), 0.0, sin(roll)), vec3(0.0, 1.0, 0.0), vec3(-sin(roll), 0.0, cos(roll)));

    return R * S * T;
}

vec2 bounding_box_intersection(vec3 ro, vec3 rd, vec3 sz) {
    vec3 m = 1.0/rd;
    vec3 n = m*ro;
    vec3 k = abs(m)*sz;
    vec3 t1 = -n - k;
    vec3 t2 = -n + k;
    float tN = max(max(t1.x, t1.y), t1.z);
    float tF = min(min(t2.x, t2.y), t2.z);
    if(tN>tF || tF<0.0) return vec2(DIST_MAX);
    return vec2(tN, tF);
}

bool get_sub_voxel(ivec3 p, int subdiv) {
    if(length(vec2(p-ivec3(subdiv))) < float(subdiv)/2.0) return false;
    
    // Sample noise texture for height offset (dependent read)
    vec2 noiseCoord = vec2(p.xz + ivec2(subdiv)) / float(subdiv * 2);
    float noise = texture(uNoiseTexture, noiseCoord).r;
    
    // Apply height offset of +/- 0.25 voxel units
    float heightOffset = (noise - 0.5) * 0.5;
    vec3 offsetPos = vec3(p - ivec3(subdiv)) + vec3(0.0, heightOffset, 0.0);
    
    return length(offsetPos * vec3(1,1,3)) < float(subdiv);
}

struct result {
    ivec3 voxel;
    bvec3 side;
};

vec3 sgn(vec3 p) {
    return step(0.0, p) * 2.0 - 1.0;
}

vec3 bounding_box_intersection_normal(vec3 ro, vec3 rd, vec3 sz, vec2 bounds) {
    vec3 norm =
        (bounds.x>0.0)
        ? // ro outside the box
        step(vec3(bounds.x),-ro/rd - sz/abs(rd))
        : // ro inside the box
        step(-ro/rd + sz/abs(rd),vec3(bounds.y));
    norm *= -sgn(rd);
    return norm;
}

result traverse_sub_voxels(vec3 ro, vec3 rd, int subdiv) {
    vec2 bounds = bounding_box_intersection(ro, rd, vec3(1));
    if(bounds.x >= DIST_MAX)
        return result(ivec3(0), bvec3(0));

    bvec3 s = bounds.x >= 0.0 ? bvec3(abs(bounding_box_intersection_normal(ro, rd, vec3(1), bounds))) : bvec3(0);

    bounds.x = max(0.0, bounds.x + 0.0001);
    ro += rd * bounds.x; // snap ray to box

    ro *= float(subdiv);

    ivec3 v = ivec3(floor(ro));
    int maxSteps = subdiv * 6;
    
    for (int i = 0; i < maxSteps; i++) {
        if(any(lessThan(v,-ivec3(subdiv))) || any(greaterThanEqual(v,+ivec3(subdiv))))
            break;

        if(get_sub_voxel(v + ivec3(subdiv), subdiv))
            break;

        vec3 c = (0.5 - sgn(rd) * (ro - vec3(v) - vec3(0.5))) * abs(rd.yzx * rd.zxy);
        s = lessThanEqual(c.xyz, min(c.yzx, c.zxy));
        v += ivec3(s) * ivec3(sgn(rd));
    }

    return result(v, s);
}

vec3 render_sub_voxels(vec3 ro, vec3 rd, int subdiv) {
    result r = traverse_sub_voxels(ro, rd, subdiv);
    if(r.voxel == ivec3(0)) return vec3(0);

    float shade = 0.5 * dot(vec3(1,4,10)/10.0, vec3(r.side));

    if(any(lessThan(r.voxel,-ivec3(subdiv))) || any(greaterThanEqual(r.voxel,+ivec3(subdiv)))) {
        // Walls and floor - keep the checkerboard pattern
        return shade * mix(vec3(0.25),vec3(0.75),vec3((r.voxel.x^r.voxel.y^r.voxel.z)&1));
    }

    // Base color texture coordinates
    vec2 colorCoord = vec2(r.voxel.xz + ivec2(subdiv)) / float(subdiv * 2);
    
    // Non-dependent read: sample color texture directly
    vec3 baseColor = texture(uColorTexture, colorCoord).rgb;
    
    // Sample noise texture to get offset for dependent read
    float noiseValue = texture(uNoiseTexture, colorCoord).r;
    
    // Dependent read: use noise value to offset color texture coordinates
    vec2 offsetCoord = fract(colorCoord + vec2(noiseValue - 0.5) * 0.1); // offset by up to ±0.05
    vec3 offsetColor = texture(uColorTexture, offsetCoord).rgb;
    
    // Blend the two color samples (50/50 mix)
    vec3 voxelColor = mix(baseColor, offsetColor, 0.5);
    
    return shade * voxelColor;
}

void main() {
    vec2 p = vUv * uResolution;
    vec2 R = uResolution;
    p = (p + p - R) / R.y;
    
    float yaw = pi/4.0;
    float pitch = acos(-sqrt(2.0/3.0));

    vec3 ro = vec3(0, 0,-2.5);
    float h = 2.0;
    vec3 rd = vec3(p, h);

    mat3 M = yaw_pitch_roll(yaw, pitch, 0.0);
    ro = M * ro;
    rd = M * rd;

    vec3 color = render_sub_voxels(ro, rd, uSubdiv);
    color = sqrt(color); // approximate gamma

    fragColor = vec4(color, 1.0);
}
